import { Container, Flex } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { Fragment, useCallback, useEffect, useMemo } from 'react';

import { LectureGrid } from '@/components/ui/features/hubs-conference/LectureGrid/LectureGrid';
import { useHubsLibraryFilters } from '@/components/ui/features/hubs-conference/LibraryFilter/HubsLibraryFilter.hooks';
import { HubsPageActions } from '@/components/ui/shared/hubs/HubsPageActions/HubsPageActions';
import { eventSortingOptions } from '@/constants/event-sorting-options';
import { lectureSortingOptions } from '@/constants/lecture-sorting-options';
import { useAnalytics } from '@/context/Analytics';
import { LectureSort } from '@/enums/LectureSort';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useStores } from '@/hooks/useStores';
import { IMetaOmnisearchProps } from '@/interfaces/search';
import { useResource, useResourceRelationship, useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { getHubBySlug, getHubsSubtree } from '@/queries/hubs';
import { fetchOmnisearchLectureAccessPermissionsSideload } from '@/queries/lecture-access-permissions';
import { queryOmnisearchLectureInfinite } from '@/queries/omnisearch-thin-lectures';
import { OmnisearchThinLecture } from '@/resources/OmnisearchThinLecture';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

const PAGE_SIZE = 12;

interface IFiltersPageContentParams {
  slug: string;
  query: string;
  sort: string;
}

export const PageContent = () => {
  const analytics = useAnalytics();
  const { store } = useStores();
  const { filters } = useHubsLibraryFilters();

  const { slug, query, sort = LectureSort.Relevance } = useQueryParams<IFiltersPageContentParams>({
    slug: StringParam,
    query: StringParam,
    sort: StringParam,
  });

  const { data: hubData } = useResource(getHubBySlug(slug));
  const {
    data: { subtreeIds },
  } = useResourceRelationship(getHubsSubtree(hubData.id));

  const { data, error, size, setSize } = useResourcesInfinite(
    queryOmnisearchLectureInfinite({
      filters: {
        library: 'true',
        scope: 'published',
        subject_id: subtreeIds,
        query,
        ...filters,
      },
      sort: sort === LectureSort.Relevance && !query ? `-${LectureSort.HeldAt}` : sort,
      pageSize: PAGE_SIZE,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      sideload: fetchOmnisearchLectureAccessPermissionsSideload(store),
    }
  );

  const { meta, ...swrExtract } = useMemo(
    () => extractSWRInfiniteData<OmnisearchThinLecture>(data as Array<Response<OmnisearchThinLecture>>, error, size),
    [data, error, size]
  );

  const isReachingEnd = useMemo(() => {
    if (!data) return false;
    const lastPage = data?.[data.length - 1].meta as IMetaOmnisearchProps;

    return lastPage.currentPage === lastPage.totalPages;
  }, [data]);

  const onLoadMore = useCallback(() => setSize(size + 1), [setSize, size]);

  useEffect(() => {
    if (query && meta?.currentPage === 1) {
      analytics.track('library_search_perform', {
        lectures_num: meta.totalCount,
        query,
      });
    }
  }, [query, meta, analytics]);

  return (
    <Fragment>
      <Container pt="1.875rem" px={{ base: 5, md: 10, lg: 5 }}>
        <Flex flexDir="column" gap={4}>
          <HubsPageActions
            sortingOptions={lectureSortingOptions}
            defaultOption={lectureSortingOptions[0].value}
            totalCount={meta?.totalCount}
          />
          <LectureGrid
            {...swrExtract}
            isReachingEnd={isReachingEnd}
            onLoadMore={onLoadMore}
            error={(error as unknown) as Error}
            pageSize={PAGE_SIZE}
          />
        </Flex>
      </Container>
    </Fragment>
  );
};
